import { useState, useEffect, useId } from 'react';
import { OverflowMenu, type OverflowMenuItem } from '../atoms/OverflowMenu';
import { useTranslation } from '../../i18n/useTranslation';
import { Button } from '../atoms';
import { Heading } from '../atoms/Typography/Heading';
import { Badge } from '../atoms/Badge';
import type { BadgeTone } from '../atoms/Badge';
import { EmptyState } from '../atoms/EmptyState';
import { Icon } from '../atoms/Icon';
import { formatDeceasedDate } from './livestockDeceased';
import { fetchLivestockImageThumbnailUrl, type LivestockRecord } from '../../services/livestockService';
import './LivestockList.css';

export interface LivestockListProps {
  items: LivestockRecord[];
  isLoading: boolean;
  onEdit: (item: LivestockRecord) => void;
  onArchive: (record: LivestockRecord) => void;
  onDelete: (record: LivestockRecord) => void;
  /** "Mark as deceased" (row menu); opens the date-of-death dialog. */
  onMarkDeceased?: (record: LivestockRecord) => void;
  /** "Undo" on a deceased animal: active again, date of death cleared. */
  onRestore?: (record: LivestockRecord) => void;
}

interface ItemActions {
  onEdit: (item: LivestockRecord) => void;
  onArchive: (record: LivestockRecord) => void;
  onDelete: (record: LivestockRecord) => void;
  onMarkDeceased?: (record: LivestockRecord) => void;
  onRestore?: (record: LivestockRecord) => void;
}

type Category = LivestockRecord['category'];

const STATUS_TONE: Record<LivestockRecord['status'], BadgeTone> = {
  active: 'success',
  archived: 'neutral',
  deceased: 'danger',
  quarantine: 'warning',
};

const CATEGORY_ORDER: Category[] = ['coral', 'fish', 'invertebrate', 'other'];

const CATEGORY_EMOJI: Record<Category, string> = {
  coral: '🪸',
  fish: '🐠',
  invertebrate: '🦀',
  other: '⚪',
};

function groupByCategory(items: LivestockRecord[]): Map<Category, LivestockRecord[]> {
  const map = new Map<Category, LivestockRecord[]>();
  for (const cat of CATEGORY_ORDER) map.set(cat, []);
  for (const item of items) {
    // An unknown category must not make the animal disappear — list it under "other".
    const list = map.get(item.category) ?? map.get('other');
    list?.push(item);
  }
  return map;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return dateStr;
  }
}

function LivestockThumbnail({
  imageIds,
  commonName,
  category,
  aquariumId,
}: {
  imageIds: readonly string[] | null | undefined;
  commonName: string;
  category: Category;
  aquariumId: string;
}) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const firstImageId = Array.isArray(imageIds) ? imageIds[0] : undefined;

  useEffect(() => {
    if (!firstImageId || !aquariumId) return;
    let cancelled = false;
    fetchLivestockImageThumbnailUrl(aquariumId, firstImageId).then((url) => {
      if (!cancelled) setImageUrl(url);
    });
    return () => { cancelled = true; };
  }, [aquariumId, firstImageId]);

  return (
    <div
      className="livestock-list__thumbnail"
      aria-hidden={imageUrl == null ? true : undefined}
    >
      {imageUrl != null ? (
        <img src={imageUrl} alt={commonName} className="livestock-list__thumbnail-img" />
      ) : (
        <span className="livestock-list__thumbnail-icon" aria-hidden="true">
          {CATEGORY_EMOJI[category] ?? CATEGORY_EMOJI.other}
        </span>
      )}
    </div>
  );
}

function LivestockItem({
  item,
  onEdit,
  onArchive,
  onDelete,
  onMarkDeceased,
  onRestore,
}: { item: LivestockRecord } & ItemActions) {
  const { t, i18n } = useTranslation();
  const isDeceased = item.status === 'deceased';
  const menuItems: OverflowMenuItem[] = [
    ...(!isDeceased && onMarkDeceased
      ? [{ id: 'deceased', label: t('livestock.deceased.markAction'), onSelect: () => onMarkDeceased(item) }]
      : []),
    ...(isDeceased && onRestore ? [{ id: 'restore', label: t('livestock.deceased.undoAction'), onSelect: () => onRestore(item) }] : []),
    ...(item.status !== 'archived' && !isDeceased
      ? [{ id: 'archive', label: t('livestock.actions.archive'), onSelect: () => onArchive(item) }]
      : []),
    { id: 'delete', label: t('livestock.actions.delete'), onSelect: () => onDelete(item), tone: 'danger' },
  ];

  return (
    <article
      className={`livestock-list__item${isDeceased ? ' livestock-list__item--deceased' : ''}`}
      data-testid="livestock-item"
      data-status={item.status}
    >
      <LivestockThumbnail
        imageIds={item.imageIds}
        commonName={item.commonName}
        category={item.category}
        aquariumId={item.aquariumId}
      />

      <div className="livestock-list__item-info">
        <div className="livestock-list__item-name-row">
          <span className="livestock-list__common-name">{item.commonName}</span>
          {isDeceased ? (
            <Badge tone="neutral" className="livestock-list__deceased-badge" data-testid="livestock-deceased-badge">
              <Icon name="flower" size="sm" className="livestock-list__deceased-icon" />
              {item.deceasedAt
                ? t('livestock.deceased.badgeOn', { date: formatDeceasedDate(item.deceasedAt, i18n.language) })
                : t('livestock.statuses.deceased')}
            </Badge>
          ) : (
            item.status !== 'active' && (
              <Badge tone={STATUS_TONE[item.status] ?? 'neutral'}>{t(`livestock.statuses.${item.status}`)}</Badge>
            )
          )}
        </div>

        {item.scientificName && (
          <span className="livestock-list__scientific-name">{item.scientificName}</span>
        )}

        <span className="livestock-list__date-added">
          <time dateTime={item.dateAdded || undefined}>{t('livestock.list.dateAdded')} {formatDate(item.dateAdded)}</time>
        </span>
      </div>

      <div className="livestock-list__actions">
        <Button size="sm" variant="secondary" onClick={() => onEdit(item)}>
          {t('livestock.actions.edit')}
        </Button>
        <OverflowMenu label={t('common.moreActionsFor', { name: item.commonName })} items={menuItems} />
      </div>
    </article>
  );
}

function CategorySection({
  category,
  items,
  ...actions
}: {
  category: Category;
  items: LivestockRecord[];
} & ItemActions) {
  const { t } = useTranslation();

  return (
    <section className="livestock-list__category-section" data-testid={`livestock-category-${category}`}>
      <header className="livestock-list__category-header">
        <Heading level={2} className="livestock-list__category-title">
          {t(`livestock.categories.${category}`)}
        </Heading>
        <span className="livestock-list__category-count">{items.length}</span>
      </header>
      <ul className="livestock-list__items">
        {items.map((item) => (
          <li key={item.id}>
            <LivestockItem item={item} {...actions} />
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Deceased animals: a separate section after the living ones, collapsed by default
 * (expanded when there is nothing else to show, e.g. with the "Deceased" filter).
 * They are not counted in the category sections.
 */
function DeceasedSection({
  items,
  defaultOpen,
  ...actions
}: { items: LivestockRecord[]; defaultOpen: boolean } & ItemActions) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  const listId = useId();

  return (
    <section className="livestock-list__category-section livestock-list__deceased-section" data-testid="livestock-deceased-section">
      <header className="livestock-list__category-header">
        <Heading level={2} className="livestock-list__category-title">
          <Button
            variant="ghost"
            size="sm"
            className="livestock-list__deceased-toggle"
            aria-expanded={open}
            aria-controls={listId}
            onClick={() => setOpen((v) => !v)}
            data-testid="livestock-deceased-toggle"
          >
            {t('livestock.deceased.section')}
            <span className="livestock-list__category-count">{items.length}</span>
          </Button>
        </Heading>
      </header>
      <ul className="livestock-list__items" id={listId} hidden={!open}>
        {items.map((item) => (
          <li key={item.id}>
            <LivestockItem item={item} {...actions} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function SkeletonItem() {
  return (
    <div className="livestock-list__skeleton-item">
      <div className="livestock-list__skeleton-thumbnail" />
      <div className="livestock-list__skeleton-body">
        <div className="livestock-list__skeleton-line livestock-list__skeleton-line--short" />
        <div className="livestock-list__skeleton-line livestock-list__skeleton-line--long" />
      </div>
    </div>
  );
}

export function LivestockList({ items, isLoading, ...actions }: LivestockListProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="livestock-list livestock-list--loading" data-testid="livestock-list-loading">
        {[1, 2, 3].map((n) => <SkeletonItem key={n} />)}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <EmptyState
        headline={t('livestock.emptyState.title')}
        description={t('livestock.emptyState.description')}
      />
    );
  }

  const living = items.filter((item) => item.status !== 'deceased');
  const deceased = items.filter((item) => item.status === 'deceased');
  const grouped = groupByCategory(living);
  const nonEmptyCategories = CATEGORY_ORDER.filter((cat) => (grouped.get(cat)?.length ?? 0) > 0);

  return (
    <div className="livestock-list" data-testid="livestock-list">
      {nonEmptyCategories.map((category) => (
        <CategorySection key={category} category={category} items={grouped.get(category) ?? []} {...actions} />
      ))}
      {deceased.length > 0 && (
        <DeceasedSection items={deceased} defaultOpen={living.length === 0} {...actions} />
      )}
    </div>
  );
}
